/*--------------------------------------------------------------
 * Data action: getEstimatedAvailability
 * Calls Retail Proxy getEstimatedAvailabilityAsync to fetch product availability
 *--------------------------------------------------------------*/
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductWarehouseInventoryAvailability } from '@msdyn365-commerce/retail-proxy';
import { getSelectedProductIdFromActionInput } from '@msdyn365-commerce-modules/retail-actions';

export class GetEstimatedAvailabilityInput implements IActionInput {
    public productId: number;
    public channelId: number;
    public catalogId?: number;

    public constructor(productId: number, channelId: number, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId;
    }

    public getCacheObjectType = (): string => 'ProductAvailabilities';

    public getCacheKey = (): string => `ProductAvailabilities|ProductId:${this.productId}|CatalogId:${this.catalogId ?? 0}`;

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (args: ICreateActionContext) => {
    const productId = getSelectedProductIdFromActionInput(args);
    const channelId = args.requestContext.apiSettings.channelId;
    const catalogId = args.requestContext ? args.requestContext.apiSettings.catalogId : 0;

    if (productId) {
        return new GetEstimatedAvailabilityInput(+productId, channelId, catalogId);
    }

    throw new Error('Unable to create GetEstimatedAvailabilityInput, no productId found');
};

export async function getEstimatedAvailabilityAction(
    input: GetEstimatedAvailabilityInput,
    context: IActionContext
): Promise<ProductWarehouseInventoryAvailability[] | null> {
    try {
        const availabilityResult = await getEstimatedAvailabilityAsync(
            { callerContext: context, queryResultSettings: {} },
            { ProductIds: [input.productId], DefaultWarehouseOnly: true }
        );

        if (availabilityResult && (availabilityResult as any).ProductWarehouseInventoryAvailabilities) {
            return (availabilityResult as any).ProductWarehouseInventoryAvailabilities as ProductWarehouseInventoryAvailability[];
        }

        return null;
    } catch (error) {
        context.telemetry.debug(`getEstimatedAvailabilityAction error: ${error}`);
        return null;
    }
}

export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/custom-product-page-summary/getEstimatedAvailability',
    action: <IAction<ProductWarehouseInventoryAvailability[] | null>>getEstimatedAvailabilityAction,
    input: createInput
});
